import React, { useState, useEffect } from "react";
import { ArrowLeft, Download, X, Share2, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { getWhatsappAccount } from "../../../../../../ApiCalls/mantisBusiness";
import { formatDateOnlyForDisplay } from "../../../../../../utils/dateUtils";

const ManifiestoDetail = ({ mantisBusinessId, mantisBusinessUserId, token, manifiestoId, onBack, onRefresh }) => {
  const [manifiesto, setManifiesto] = useState(null);
  const [remesas, setRemesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  useEffect(() => {
    loadManifiesto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifiestoId]);

  const loadManifiesto = async () => {
    setLoading(true);
    try {
      // Load manifiesto data and remesas in parallel
      const [manifiestoRes, remesasRes] = await Promise.all([
        roldanApi.getManifiestoById(mantisBusinessId, token, manifiestoId),
        roldanApi.getRemesasByManifiesto(mantisBusinessId, token, manifiestoId).catch(() => ({ success: false }))
      ]);
      
      if (manifiestoRes?.success) {
        setManifiesto(manifiestoRes.data);
      }
      
      // Always prioritize remesas from the dedicated endpoint (manifiesto_remesas junction table)
      if (remesasRes?.success && remesasRes.data && Array.isArray(remesasRes.data)) {
        const loadedRemesas = remesasRes.data;
        console.log('Loaded remesas from manifiesto_remesas junction table:', loadedRemesas.length);
        
        // Show remesas if we successfully loaded them from the endpoint
        // The endpoint response is the source of truth, not cantidad_remesas
        if (loadedRemesas.length > 0) {
          setRemesas(loadedRemesas);
          // Update cantidad_remesas if it's incorrect in the manifiesto data
          if (manifiestoRes?.data && manifiestoRes.data.cantidad_remesas !== loadedRemesas.length) {
            console.log('Updating cantidad_remesas from', manifiestoRes.data.cantidad_remesas, 'to', loadedRemesas.length);
            setManifiesto({
              ...manifiestoRes.data,
              cantidad_remesas: loadedRemesas.length
            });
          }
        } else {
          setRemesas([]);
        }
      } else {
        // Endpoint doesn't exist or failed - check console for error
        console.error('getRemesasByManifiesto failed:', remesasRes);
        console.warn('getRemesasByManifiesto endpoint not available or failed. Not showing remesas to avoid showing pre-manifiesto remesas.');
        setRemesas([]);
      }
    } catch (error) {
      console.error("Error loading manifiesto:", error);
      setRemesas([]);
    } finally {
      setLoading(false);
    }
  };


  
  const handleOpenWhatsappModal = () => {
    // Set initial phone number from first remesa's conductor if available
    const initialPhone = remesas.length > 0 && remesas[0].celular ? remesas[0].celular : "";
    setPhoneNumber(initialPhone);
    setShowWhatsappModal(true);
  };

  const handleCloseWhatsappModal = () => {
    setShowWhatsappModal(false);
    setPhoneNumber("");
  };

  const handleShareWhatsApp = async () => {
    if (!phoneNumber || !phoneNumber.trim()) {
      alert("Por favor ingrese un número de teléfono");
      return;
    }

    setSendingWhatsapp(true);
    try {
      // Get WhatsApp account for the business
      const whatsappAccountRes = await getWhatsappAccount(token, mantisBusinessId);
      
      if (!whatsappAccountRes.success || !whatsappAccountRes.account) {
        alert("No hay una cuenta de WhatsApp configurada. Por favor configure una cuenta en Configuración > Integraciones.");
        setSendingWhatsapp(false);
        return;
      }

      const whatsappAccount = whatsappAccountRes.account;

      // Call the share manifiesto endpoint
      const res = await roldanApi.shareManifiestoWhatsApp(
        mantisBusinessId,
        token,
        manifiestoId,
        phoneNumber.trim(),
        whatsappAccount
      );

      if (res?.success) {
        alert(`Manifiesto ${manifiesto.numero_manifiesto_erp || manifiestoId} compartido exitosamente a ${phoneNumber}`);
        handleCloseWhatsappModal();
      } else {
        alert(res?.error || res?.message || "Error al compartir el manifiesto");
      }
    } catch (error) {
      console.error("Error sharing manifiesto:", error);
      alert("Error al compartir el manifiesto: " + (error.message || "Error desconocido"));
    } finally {
      setSendingWhatsapp(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  if (!manifiesto) return <div className="p-8 text-center text-red-600">Manifiesto no encontrado</div>;

  const chipCls = (estado) => {
    const e = (estado || '').toLowerCase();
    if (e.includes('activo')) return 'bg-emerald-100 text-emerald-700';
    if (e.includes('anulado')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="px-0 sm:px-2 overflow-x-hidden">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 border-b border-gray-100">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 w-full sm:w-auto justify-center sm:justify-start"
          >
            <ArrowLeft size={18} /> Volver
          </button>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            {/* {manifiesto.estado === "Activo" && (
              <button
                onClick={handleAnular}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-red-200 text-red-700 hover:bg-red-50 rounded-lg w-full sm:w-auto"
              >
                <X size={16} /> Anular Manifiesto
              </button>
            )} */}
            <button
              onClick={handleOpenWhatsappModal}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50 w-full sm:w-auto"
            >
              <Share2 size={16} /> Compartir por WhatsApp
            </button>
            <button
              onClick={async () => {
                setDownloadingPDF(true);
                try {
                  await roldanApi.downloadManifiestoPDF(mantisBusinessId, token, manifiestoId);
                } catch (error) {
                  console.error("Error downloading PDF:", error);
                  alert("Error al descargar el PDF: " + (error.message || "Error desconocido"));
                } finally {
                  setDownloadingPDF(false);
                }
              }}
              disabled={downloadingPDF}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {downloadingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                  Generando PDF...
                </>
              ) : (
                <>
                  <Download size={16} /> Descargar PDF
                </>
              )}
            </button>
          </div>
        </div>

        <div className="p-2 sm:p-4 grid gap-4 overflow-x-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-100 p-3 sm:p-4 bg-gray-50 overflow-hidden">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Información General
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-800 break-words">
                <div className="break-words min-w-0">
                  <span className="text-gray-500 font-semibold block sm:inline">
                    N° Manifiesto ERP:
                  </span>{" "}
                  <span className="font-mono font-semibold text-gray-900 break-all block sm:inline">
                    {manifiesto.numero_manifiesto_erp || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">Estado:</span>{" "}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold text-xs ${chipCls(manifiesto.estado)}`}>
                    {manifiesto.estado || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">
                    Fecha Creación:
                  </span>{" "}
                  {manifiesto.fecha_creacion_erp
                    ? new Date(manifiesto.fecha_creacion_erp).toLocaleDateString()
                    : "N/A"}
                </div>
                <div>
                  <span className="text-gray-500 font-semibold">
                    Cantidad Remesas:
                  </span>{" "}
                  {manifiesto.cantidad_remesas || remesas.length || 0}
                </div>
                {manifiesto.autorizacion != null && String(manifiesto.autorizacion).trim() !== "" && (
                  <div className="col-span-1 sm:col-span-2 flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-500 font-semibold">Autorización RNDC:</span>{" "}
                    <span className="font-mono text-emerald-700 font-medium break-all">
                      {String(manifiesto.autorizacion).trim()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 p-3 sm:p-4 bg-gray-50 overflow-hidden">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Pre-Manifiesto
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-800 break-words">
                <div className="col-span-1 sm:col-span-2 break-words min-w-0">
                  <span className="text-gray-500 font-semibold block sm:inline">N° Pre-Manifiesto:</span>{" "}
                  <span className="font-mono font-semibold text-gray-900 break-all block sm:inline">
                    {manifiesto.numero_pre_manifiesto || "N/A"}
                  </span>
                </div>
                {manifiesto.fecha_viaje && (
                  <div className="min-w-0">
                    <span className="text-gray-500 font-semibold block sm:inline">Fecha Viaje:</span>{" "}
                    <span className="block sm:inline">{formatDateOnlyForDisplay(manifiesto.fecha_viaje)}</span>
                  </div>
                )}
                {manifiesto.ruta && (
                  <div className="break-words min-w-0 sm:col-span-1">
                    <span className="text-gray-500 font-semibold block sm:inline">Ruta:</span>{" "}
                    <span className="break-all block sm:inline">{manifiesto.ruta}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {manifiesto.motivo_anulacion && (
            <div className="rounded-lg border border-red-200 p-3 sm:p-4 bg-red-50 overflow-hidden">
              <h2 className="text-sm font-bold text-red-900 mb-2">
                Motivo de Anulación
              </h2>
              <p className="text-sm text-red-800 break-words">{manifiesto.motivo_anulacion}</p>
            </div>
          )}

          {remesas.length > 0 && (
            <div className="rounded-lg border border-gray-100 p-3 sm:p-4 bg-gray-50 overflow-hidden">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                Remesas del Manifiesto ({remesas.length})
              </h2>
              <div className="overflow-x-auto -mx-3 sm:-mx-4">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600">
                        <th className="text-left font-semibold px-2 sm:px-4 py-2 whitespace-nowrap">N° Remesa</th>
                        <th className="text-left font-semibold px-2 sm:px-4 py-2 whitespace-nowrap min-w-[100px]">Cliente</th>
                        <th className="text-left font-semibold px-2 sm:px-4 py-2 whitespace-nowrap min-w-[120px]">Conductor</th>
                        <th className="text-left font-semibold px-2 sm:px-4 py-2 whitespace-nowrap min-w-[100px]">Ruta</th>
                        <th className="text-left font-semibold px-2 sm:px-4 py-2 whitespace-nowrap">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {remesas.map((remesa) => (
                        <tr key={remesa.remesa_id} className="bg-white">
                          <td className="px-2 sm:px-4 py-2 font-mono font-semibold text-gray-900 break-all whitespace-normal">
                            {remesa.numero_remesa}
                          </td>
                          <td className="px-2 sm:px-4 py-2 text-gray-800 break-words whitespace-normal">{remesa.cliente || "N/A"}</td>
                          <td className="px-2 sm:px-4 py-2 text-gray-800 break-words whitespace-normal">{remesa.nombre_conductor || "N/A"}</td>
                          <td className="px-2 sm:px-4 py-2 text-gray-800 break-words whitespace-normal">{remesa.ruta || "N/A"}</td>
                          <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-xs">
                              {remesa.estado || "N/A"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-100 p-3 sm:p-4 bg-gray-50 overflow-hidden">
            <h2 className="text-sm font-bold text-gray-900 mb-2">Documentos</h2>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={async () => {
                  setDownloadingPDF(true);
                  try {
                    await roldanApi.downloadManifiestoPDF(mantisBusinessId, token, manifiestoId);
                  } catch (error) {
                    console.error("Error downloading PDF:", error);
                    alert("Error al descargar el PDF: " + (error.message || "Error desconocido"));
                  } finally {
                    setDownloadingPDF(false);
                  }
                }}
                disabled={downloadingPDF}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    Generando PDF...
                  </>
                ) : (
                  <>
                    <Download size={16} /> Descargar Manifiesto (PDF)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Share Modal */}
      {showWhatsappModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Compartir por WhatsApp
              </h3>
              <button
                onClick={handleCloseWhatsappModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de teléfono (con código de país)
              </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Ej: +573001234567"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={sendingWhatsapp}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseWhatsappModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={sendingWhatsapp}
              >
                Cancelar
              </button>
              <button
                onClick={handleShareWhatsApp}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sendingWhatsapp || !phoneNumber.trim()}
              >
                {sendingWhatsapp ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManifiestoDetail;

